import { useReactiveVar } from '@apollo/client';
import { tosPpStore } from 'apolloGraphql/mml-apollo-cache';
import { logger } from 'logger';
import { startTransition, useCallback, useEffect, useState } from 'react';
import { getValue, removeItem, setValue } from 'utils/localStorage';

const LOG = logger.getLogger('useUserConsent');

/**
 * Custom events emitted by UserConsent library
 * https://github.com/turnercode/user-consent?tab=readme-ov-file#events
 */
enum UserConsentCustomEvents {
  AcknowledgeTermsChanged = 'ackTermsChanged',
  Ready = 'userConsentReady',
}

export default function useUserConsent() {
  // Loaded means uc is ready
  const [loaded, setLoaded] = useState(false);
  const tosPp = useReactiveVar(tosPpStore);
  // Determine if tosPp value has changed, we re-show the tos modal if so
  const isTosPpDiff = getValue('tosPpLocal') !== tosPp + '';
  // Ready means uc is ready and we have all data needed
  const ready = loaded && !!tosPp;

  /**
   * Check if legal terms acknowledgment is needed via UserConsent library
   */
  const checkLegalTermsRequired = useCallback(() => {
    try {
      if (!window.WBD?.UserConsent.getAckTermsNeeded()) {
        return false;
      }

      return true;
    } catch (error) {
      LOG.error('Error checking if legal terms are required:', error);
    }

    return false;
  }, []);

  const showTosPp = loaded && !!tosPp && (isTosPpDiff || checkLegalTermsRequired());

  /**
   * Acknowledge legal terms via UserConsent library
   */
  const ackLegalTerms = useCallback((): void => {
    try {
      if (!window?.WBD?.UserConsent?.ackTerms) {
        LOG.warn('UserConsent.ackTerms not available');
        return;
      }
      // Normal user acknowledgment
      window.WBD.UserConsent.ackTerms();
    } catch (error) {
      LOG.error('Error acknowledging legal terms:', error);
    }
  }, []);

  /**
   * Methods to call when UserConsent library is ready
   */
  function onReady() {
    // startTransition prevents suspense issue
    startTransition(() => {
      setLoaded(true);
    });
  }

  /**
   * Loads userconsent library and waits for it to be ready
   * true if loaded successfully, false if timeout/error
   * promise rejects if timeout/error
   */
  const loadUserConsent = useCallback(async (): Promise<boolean> => {
    if (!window?.WBD?.UserConsent) {
      LOG.warn('UserConsent library not available during init.');
      return false;
    }

    if (window.WBD.UserConsent.isReady()) {
      onReady();
      return true;
    }

    let timeout: ReturnType<typeof setTimeout>;

    return new Promise<boolean>((resolve, reject) => {
      // add listener
      document.addEventListener(
        UserConsentCustomEvents.Ready,
        () => {
          onReady();
          resolve(true);
        },
        { once: true },
      );

      // timout after 5s
      timeout = setTimeout(() => {
        reject('failed to load UserConsent library within timeout period');
      }, 5000);
    }).finally(() => {
      if (timeout) {
        clearTimeout(timeout);
      }
    });
  }, []);

  /**
   * Initialize with UserConsent library
   * This should only be called once on app startup
   */
  const initUserConsent = useCallback(async () => {
    try {
      await loadUserConsent();
    } catch (error) {
      LOG.error('Error during UserConsent init:', error);
    }
  }, [loadUserConsent]);

  useEffect(() => {
    // When the tospp value is different than before, consent
    // needs to be re-accepted
    if (loaded && tosPp && isTosPpDiff && !checkLegalTermsRequired()) {
      // Update our local value
      setValue('tosPpLocal', tosPp + '');
      // Delete local storage consent
      removeItem('wbdLTP');
      // Reload and show consent again
      window.WBD?.UserConsent.forceReconsent();
    }
  }, [checkLegalTermsRequired, isTosPpDiff, loaded, tosPp]);

  return {
    ready,
    showTosPp,
    initUserConsent,
    ackLegalTerms,
    checkLegalTermsRequired,
  };
}
